import { useMemo, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoMeta from '@/components/feature/SeoMeta';
import { videos, categories } from '@/mocks/videos';

function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return views.toString();
}

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const video = useMemo(() => videos.find((v) => v.id === Number(id)), [id]);
  const categoryName = useMemo(
    () => categories.find((c) => c.slug === video?.category)?.name || '',
    [video],
  );

  const relatedVideos = useMemo(
    () =>
      video
        ? videos.filter((v) => v.category === video.category && v.id !== video.id).slice(0, 4)
        : [],
    [video],
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!video) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-900 mb-2">视频未找到</h1>
            <p className="text-foreground-600 mb-6">抱歉，您要查看的51黑料视频不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: video.title,
        description: video.description,
        datePublished: video.date,
        url: `https://www.51heiliao.com/article/${video.id}`,
        author: { '@type': 'Organization', name: '51黑料' },
        publisher: { '@type': 'Organization', name: '51黑料' },
        articleBody: video.content?.replace(/<[^>]*>/g, '') || video.description,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.51heiliao.com/' },
          { '@type': 'ListItem', position: 2, name: categoryName, item: `https://www.51heiliao.com/category/${video.category}` },
          { '@type': 'ListItem', position: 3, name: video.title, item: `https://www.51heiliao.com/article/${video.id}` },
        ],
      },
      {
        '@type': 'VideoObject',
        name: video.title,
        description: video.description,
        thumbnailUrl: video.thumbnail,
        uploadDate: video.date,
        contentUrl: `https://www.51heiliao.com/article/${video.id}`,
      },
    ],
  };

  return (
    <>
      <SeoMeta
        title={`${video.title} - 51黑料在线观看 | 51黑料网`}
        description={video.description}
        keywords={`${video.tags?.join(',') || '51黑料'},51黑料在线观看,51黑料视频,${categoryName}`}
        canonical={`https://www.51heiliao.com/article/${video.id}`}
        ogType="article"
        schema={articleSchema}
      />

      <Navbar />

      <article className="pt-20 pb-16">
        <div className="w-full max-w-5xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <nav className="mb-6" aria-label="面包屑导航">
            <ol className="flex items-center text-xs text-foreground-500 gap-1.5 flex-wrap">
              <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li>
                <Link to={`/category/${video.category}`} className="hover:text-primary-500 transition-colors cursor-pointer">
                  {categoryName}
                </Link>
              </li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-foreground-700 truncate max-w-[200px]">{video.title}</li>
            </ol>
          </nav>

          {/* Title */}
          <header className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading leading-snug">
              <strong>{video.title}</strong>
            </h1>
            <div className="flex items-center flex-wrap gap-4 mt-4 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {video.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {formatViews(video.views)} 次播放
              </span>
              <span className="px-3 py-0.5 rounded-md bg-primary-50 text-primary-500 text-xs font-medium">
                {categoryName}
              </span>
              {video.isHot && (
                <span className="px-3 py-0.5 rounded-md bg-accent-100 text-accent-700 text-xs font-medium flex items-center gap-1">
                  <i className="ri-fire-line"></i>
                  热门
                </span>
              )}
            </div>
          </header>

          {/* Video Player Area */}
          <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-black mb-8">
            <img
              src={video.thumbnail}
              alt={video.title}
              className="w-full h-full object-cover"
              title={`51黑料在线观看 - ${video.title}`}
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <button
                onClick={() => navigate(`/article/${video.id}`)}
                className="w-20 h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 hover:bg-primary-500 hover:scale-110 transition-all duration-300 cursor-pointer"
              >
                <i className="ri-play-fill text-4xl ml-1"></i>
              </button>
            </div>
            <span className="absolute bottom-4 right-4 px-3 py-1 bg-black/70 text-white text-sm rounded-md">
              {video.duration}
            </span>
          </div>

          {/* Tags */}
          {video.tags && video.tags.length > 0 && (
            <div className="flex items-center flex-wrap gap-2 mb-8">
              {video.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-700 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Content */}
          <section className="prose-custom max-w-none">
            <div
              className="text-foreground-800 leading-relaxed text-sm md:text-base space-y-4"
              dangerouslySetInnerHTML={{ __html: video.content || `<p>${video.description}</p>` }}
            />
          </section>

          {/* Navigation */}
          <nav className="flex items-center justify-between mt-12 pt-8 border-t border-background-200/70">
            <Link
              to="/"
              className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
            <Link
              to={`/category/${video.category}`}
              className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
            >
              更多{categoryName}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </nav>

          {/* Related Videos */}
          {relatedVideos.length > 0 && (
            <section className="mt-12">
              <h2 className="text-xl font-semibold text-foreground-900 font-heading mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {relatedVideos.map((rv) => (
                  <Link
                    key={rv.id}
                    to={`/article/${rv.id}`}
                    className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300/50 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full aspect-video overflow-hidden">
                      <img
                        src={rv.thumbnail}
                        alt={rv.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        title={rv.title}
                      />
                      <span className="absolute bottom-2 right-2 px-2 py-0.5 text-xs font-medium bg-black/70 text-white rounded-md">
                        {rv.duration}
                      </span>
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                        {rv.title}
                      </h3>
                      <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {formatViews(rv.views)}
                        </span>
                        <span>{rv.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      <Footer />
    </>
  );
}